/**
 * REDEX Cart Store
 * Zustand store for shopping cart with localStorage persistence
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SKUS, getSku, formatPrice, separateByType, type Sku } from '../lib/catalog';

// ============================================
// TYPES
// ============================================

export interface CartItem {
  skuId: string;
  qty: number;
}

export interface CartState {
  items: CartItem[];
  version: number;
}

export interface CartActions {
  add: (skuId: string, qty?: number) => void;
  remove: (skuId: string) => void;
  setQty: (skuId: string, qty: number) => void;
  clear: () => void;
  getTotal: () => number;
  getItemCount: () => number;
  getItemsWithDetails: () => CartItemWithDetails[];
  hasSubscription: () => boolean;
  hasMixedTypes: () => boolean;
}

export interface CartItemWithDetails extends CartItem {
  sku: Sku;
  subtotal: number;
}

// ============================================
// STORE
// ============================================

const CART_VERSION = 1;

export const useCartStore = create<CartState & CartActions>()(
  persist(
    (set, get) => ({
      // State
      items: [],
      version: CART_VERSION,

      // Actions
      add: (skuId: string, qty = 1) => {
        const sku = getSku(skuId);
        if (!sku) {
          console.warn(`SKU not found: ${skuId}`);
          return;
        }

        set(state => {
          const existingIndex = state.items.findIndex(item => item.skuId === skuId);

          if (existingIndex >= 0) {
            // Update quantity
            const newItems = [...state.items];
            newItems[existingIndex] = {
              ...newItems[existingIndex],
              qty: newItems[existingIndex].qty + qty,
            };
            return { items: newItems };
          }

          // Add new item
          return { items: [...state.items, { skuId, qty }] };
        });
      },

      remove: (skuId: string) => {
        set(state => ({
          items: state.items.filter(item => item.skuId !== skuId),
        }));
      },

      setQty: (skuId: string, qty: number) => {
        if (qty <= 0) {
          get().remove(skuId);
          return;
        }

        set(state => {
          const existingIndex = state.items.findIndex(item => item.skuId === skuId);

          if (existingIndex >= 0) {
            const newItems = [...state.items];
            newItems[existingIndex] = { ...newItems[existingIndex], qty };
            return { items: newItems };
          }

          // Item doesn't exist, add it
          return { items: [...state.items, { skuId, qty }] };
        });
      },

      clear: () => {
        set({ items: [] });
      },

      getTotal: () => {
        const { items } = get();
        return items.reduce((total, item) => {
          const sku = getSku(item.skuId);
          if (!sku) return total;
          return total + sku.unitAmount * item.qty;
        }, 0);
      },

      getItemCount: () => {
        const { items } = get();
        return items.reduce((count, item) => count + item.qty, 0);
      },

      getItemsWithDetails: (): CartItemWithDetails[] => {
        const { items } = get();
        return items
          .map(item => {
            const sku = getSku(item.skuId);
            if (!sku) return null;
            return {
              ...item,
              sku,
              subtotal: sku.unitAmount * item.qty,
            };
          })
          .filter((item): item is CartItemWithDetails => item !== null);
      },

      hasSubscription: () => {
        const { items } = get();
        return items.some(item => {
          const sku = getSku(item.skuId);
          return sku?.type === 'subscription';
        });
      },

      hasMixedTypes: () => {
        const { items } = get();
        const skuIds = items.map(item => item.skuId);
        const { oneTime, subscription } = separateByType(skuIds);
        return oneTime.length > 0 && subscription.length > 0;
      },
    }),
    {
      name: 'redex-cart',
      storage: createJSONStorage(() => localStorage),
      version: CART_VERSION,
      migrate: (persistedState: unknown, version: number) => {
        // Handle migrations between versions if needed
        if (version < CART_VERSION) {
          return { items: [], version: CART_VERSION };
        }
        return persistedState as CartState & CartActions;
      },
    }
  )
);

// ============================================
// SELECTOR HOOKS
// ============================================

export const useCartItems = () => useCartStore(state => state.items);
export const useCartTotal = () => useCartStore(state => state.getTotal());
export const useCartItemCount = () => useCartStore(state => state.getItemCount());
export const useCartItemsWithDetails = () => useCartStore(state => state.getItemsWithDetails());

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Add bundle to cart
 */
export function addBundleToCart(bundleId: string) {
  const cart = useCartStore.getState();
  
  // Import bundle definitions
  const { BUNDLES, getSku } = require('../lib/catalog');
  const bundle = BUNDLES[bundleId];
  
  if (!bundle) {
    console.warn(`Bundle not found: ${bundleId}`);
    return;
  }

  // Add each SKU in the bundle
  bundle.skuIds.forEach((skuId: string) => {
    const sku = getSku(skuId);
    if (sku) {
      cart.add(skuId, 1);
    }
  });
}

/**
 * Get formatted cart total
 */
export function getFormattedCartTotal(): string {
  const total = useCartStore.getState().getTotal();
  return formatPrice(total);
}
